import type { TableBaseProps } from '../TableBase';
import { TableBase } from '../TableBase';

export type TableData = string | JSX.Element;

interface Header {
  data: [TableData] | [TableData, TableData];
  onClickEdit?: () => void;
  disableEditButton?: boolean;
}

export type { Header as InformationTableHeader };

export interface Row {
  data: [TableData] | [TableData, TableData];
  testID?: string;
  onClickEdit?: () => void;
  onClickRow?: () => void;
  backgroundColor?: 'transparent' | 'standard' | 'primary-hover' | 'highlight';
  borderTop?: 'default' | 'divider';
  disableEditButton?: boolean;
  disableFormatting?: boolean;
}

export interface InformationTableProps extends TableBaseProps {
  header?: Header;
  /**
   * Use if you need to have a custom header
   */
  renderHeader?: JSX.Element;
  rows: Row[];
  testID?: string;
  column1FlexBasis?: number;
  /**
   * Manual intervention of column 1 width vs columns 2
   */
  background?: 'transparent' | 'standard';
}

/**
 * @deprecated -- This table has visual issues regarding alignments and other things. Think about using the BasicTableWithEditButton instead, it might be a better fit
 */
export function InformationTable({
  header,
  renderHeader,
  rows,
  testID,
  column1FlexBasis = 50,
  background = 'standard',
  ...tableBaseProps
}: InformationTableProps): JSX.Element {
  const rowIsEditable = (row: Row) => 'onClickEdit' in row;

  return (
    <TableBase background={background} testID={testID} {...tableBaseProps}>
      {/* HEADER */}
      {header || renderHeader ? (
        <TableBase.Head testID={`${testID ? `${testID}-` : ''}header`}>
          {renderHeader && !header ? (
            <TableBase.Row>
              <TableBase.Header>{renderHeader}</TableBase.Header>
            </TableBase.Row>
          ) : null}
          {header ? (
            <TableBase.Row>
              {header.data.length === 1 ? (
                <TableBase.Header>{renderComponentOrString(header.data[0], TableBase.HeaderText)}</TableBase.Header>
              ) : (
                <>
                  <TableBase.Header minWidth="20%" flexBasis={`${column1FlexBasis}%`}>
                    {renderComponentOrString(header.data[0], (props) => (
                      <TableBase.HeaderText textAlign="left">{props.children}</TableBase.HeaderText>
                    ))}
                  </TableBase.Header>
                  <TableBase.Header justifyContent="flex-end" minWidth="20%" flexBasis={`${100 - column1FlexBasis}%`}>
                    {renderComponentOrString(header.data[1], (props) => (
                      <TableBase.HeaderText textAlign="right">{props.children}</TableBase.HeaderText>
                    ))}
                  </TableBase.Header>
                </>
              )}
              {header.onClickEdit ? (
                <TableBase.Header flexGrow={0} marginLeft="$lg" justifyContent="center" minWidth="$size.xs">
                  <TableBase.EditButton
                    onPress={header.onClickEdit}
                    testID={`${testID ? `${testID}-` : ''}header-edit`}
                    disabled={!!header.disableEditButton}
                  />
                </TableBase.Header>
              ) : null}
            </TableBase.Row>
          ) : null}
        </TableBase.Head>
      ) : null}

      <TableBase.Body>
        {rows.map((row, index) => {
          const borderBottomWidth = !isLastIndex(index, rows) ? 1 : 0;

          return row.disableFormatting ? (
            renderComponentOrString(row.data[0], TableBase.KeyText)
          ) : (
            <TableBase.RowHighlighted
              testID={row.testID ? `${row.testID}-row` : `${testID ? `${testID}-` : ''}row-${index}`}
              key={index}
              borderBottomWidth={borderBottomWidth}
              hoverStyle={{
                background: row.onClickRow ? 'primary-hover' : row.backgroundColor,
              }}
              cursor={row.onClickRow ? 'pointer' : 'default'}
              onPress={row.onClickRow}
              background={row.backgroundColor}
              borderTop={row.borderTop}
              width="100%"
              flexDirection="column"
              $tablet={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '$xl',
              }}
            >
              <TableBase.Data
                $tablet={{
                  minWidth: '20%',
                  flexBasis: `${column1FlexBasis}%`,
                }}
              >
                {renderComponentOrString(row.data[0], TableBase.KeyText)}
              </TableBase.Data>
              {row.data[1] ? (
                <TableBase.Data
                  gap="$lg"
                  justifyContent="space-between"
                  $tablet={{
                    minWidth: '20%',
                    flexBasis: `${100 - column1FlexBasis}%`,
                    justifyContent: 'flex-end',
                  }}
                >
                  {renderComponentOrString(row.data[1], TableBase.ValueText)}
                  {rowIsEditable(row) ? (
                    <TableBase.EditButton
                      onPress={row.onClickEdit}
                      testID={`${testID ? `${testID}-` : ''}row-${index}-edit`}
                      disabled={!!row.disableEditButton}
                    />
                  ) : null}
                </TableBase.Data>
              ) : null}
            </TableBase.RowHighlighted>
          );
        })}
      </TableBase.Body>
    </TableBase>
  );
}

function isLastIndex<T>(index: number, array: T[]) {
  return index === array.length - 1;
}
const renderComponentOrString = (value: TableData, TextWrapper: React.FC<{ children: React.ReactNode }>) => {
  return typeof value !== 'string' ? value : <TextWrapper>{value}</TextWrapper>;
};
